import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getCustomerInfo, getActiveSubscriptionDetails } from '../services/revenuecat';
import { cancelStripeSubscription, resumeStripeSubscription } from '../services/stripe';
import { clearSubscriptionCache, saveSubscriptionToCache, getSubscriptionFromCache } from '../utils/subscriptionCache';
import { getCurrencySymbol } from '../utils/currency';
import { trackSubscriptionCancelled, trackSubscriptionCancellationFailed } from '../services/analytics';
import { Deal, dealFromMap } from '../types/deal';
import { trackStoreLinkClick } from '../services/unifiedAnalytics';
import { buildAppMagicLink } from '../services/appLink';
import './SubscriptionDetails.css';
import LoadingRive from './LoadingRive';
import DealBanner from './DealBanner';
import { t } from '../i18n';

const logo = `${process.env.PUBLIC_URL}/logo.svg`;

const SubscriptionDetails: React.FC = () => {
  const { currentUser, userDocData, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);
  const [deal, setDeal] = useState<Deal | null>(null);
  const [error, setError] = useState<string>('');
  const [cancelling, setCancelling] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [magicLink, setMagicLink] = useState('');

  // Build the app magic link (login with email pre-filled) for the download CTA.
  useEffect(() => {
    buildAppMagicLink(currentUser?.email).then(setMagicLink);
  }, [currentUser]);

  useEffect(() => {
    const loadSubscriptionDetails = async () => {
      if (!currentUser?.uid) {
        navigate('/login');
        return;
      }

      try {
        // Check if we just came from a successful subscription creation
        // If so, skip cache and fetch fresh data
        const searchParams = new URLSearchParams(window.location.search);
        const skipCache = searchParams.get('refresh') === 'true';

        // Try to load from cache first (unless we just created a subscription)
        const cachedDetails = !skipCache ? getSubscriptionFromCache(currentUser.uid) : null;
        if (cachedDetails) {
          console.log('Loading subscription from cache');
          setSubscriptionDetails(cachedDetails);
          setLoading(false);

          // Still check for active deal from user document
          const activeDeal = userDocData?.settings?.active_deal;
          if (activeDeal) {
            const dealObj = dealFromMap(activeDeal);
            setDeal(dealObj);
            console.log('Deal from userDocData:', dealObj);
          }
          return;
        }

        // Cache miss, fetch fresh data
        console.log('Cache miss or refresh requested, fetching fresh subscription data');
        const subscriber = await getCustomerInfo(currentUser.uid);
        console.log('Subscriber data:', subscriber);
        const details = getActiveSubscriptionDetails(subscriber);
        console.log('Subscription details:', details);

        if (!details) {
          // No active subscription, redirect to offers
          navigate('/offers');
          return;
        }

        // Get active deal from user document if available
        const activeDeal = userDocData?.settings?.active_deal;
        if (activeDeal) {
          const dealObj = dealFromMap(activeDeal);
          setDeal(dealObj);
          console.log('Deal from userDocData:', dealObj);
        }

        // Save to cache
        saveSubscriptionToCache(currentUser.uid, details);
        setSubscriptionDetails(details);

        // Remove the refresh parameter from URL if it existed
        if (skipCache) {
          window.history.replaceState({}, document.title, '/subscription');
        }
      } catch (err) {
        console.error('Error loading subscription details:', err);
        setError(t('subscription.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    loadSubscriptionDetails();
  }, [currentUser, navigate, userDocData]);

  // Update deal whenever userDocData active_deal changes
  useEffect(() => {
    if (userDocData?.settings?.active_deal) {
      const dealObj = dealFromMap(userDocData.settings.active_deal);
      setDeal(dealObj);
      console.log('Deal banner updated from userDocData:', dealObj);
    }
  }, [userDocData?.settings?.active_deal]);

  const handleCancel = async () => {
    // Check if this is a promotional subscription (can't be cancelled via Stripe)
    if (subscriptionDetails?.store === 'promotional') {
      alert(t('subscription.promotionalAlert'));
      return;
    }

    // Check if this is a Stripe subscription
    if (subscriptionDetails?.store !== 'stripe') {
      alert(t('subscription.nonStripeAlert'));
      return;
    }

    // Confirm cancellation
    const confirmed = window.confirm(t('subscription.confirmCancel'));

    if (!confirmed) {
      return;
    }

    setCancelling(true);
    setError('');

    try {
      // Get the Stripe subscription ID from RevenueCat
      const stripeSubscriptionId = subscriptionDetails?.stripeSubscriptionId;

      console.log('Subscription details:', subscriptionDetails);
      console.log('Stripe subscription ID:', stripeSubscriptionId);
      console.log('Subscription ID type:', typeof stripeSubscriptionId);
      console.log('Subscription store:', subscriptionDetails?.store);

      // Validate subscription ID exists
      if (!stripeSubscriptionId) {
        console.error('No subscription ID found in:', subscriptionDetails);
        setError(t('subscription.noSubscriptionId'));
        setCancelling(false);
        return;
      }

      // Convert to string if needed and trim whitespace
      const subscriptionIdString = String(stripeSubscriptionId).trim();

      console.log('Sending subscription ID to backend:', subscriptionIdString);
      console.log('Subscription ID length:', subscriptionIdString.length);
      console.log('Subscription ID starts with sub_:', subscriptionIdString.startsWith('sub_'));

      console.log('Cancelling subscription:', subscriptionIdString);

      const result = await cancelStripeSubscription(
        currentUser?.uid || '',
        subscriptionIdString,
        true // Cancel at period end
      );

      console.log('Cancellation result:', result);

      if (result?.success === true) {
        // Track subscription cancellation
        trackSubscriptionCancelled(
          subscriptionDetails?.productIdentifier || 'Unknown Plan',
          currentUser?.email || undefined,
          currentUser?.uid,
          'User requested cancellation'
        );

        alert(result.message || t('subscription.cancelledDefault'));
        // Clear cache and reload subscription details to show updated status
        clearSubscriptionCache(currentUser!.uid);
        const subscriber = await getCustomerInfo(currentUser!.uid);
        const details = getActiveSubscriptionDetails(subscriber);
        if (details) {
          saveSubscriptionToCache(currentUser!.uid, details);
        }
        setSubscriptionDetails(details);
      } else {
        // Handle both result.success === false and result === null
        const errorMessage = result?.message || t('subscription.failedToCancel');
        console.log('Cancellation failed, tracking error:', errorMessage);
        setError(errorMessage);

        // Track failed cancellation
        trackSubscriptionCancellationFailed(
          subscriptionDetails?.productIdentifier || 'Unknown Plan',
          errorMessage,
          currentUser?.email || undefined,
          currentUser?.uid
        );
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      const errorMessage = t('subscription.failedToCancel');
      setError(errorMessage);

      // Track failed cancellation
      trackSubscriptionCancellationFailed(
        subscriptionDetails?.productIdentifier || 'Unknown Plan',
        err instanceof Error ? err.message : errorMessage,
        currentUser?.email || undefined,
        currentUser?.uid
      );
    } finally {
      setCancelling(false);
    }
  };

  const handleResume = async () => {
    // Confirm resumption
    const confirmed = window.confirm(t('subscription.confirmResume'));

    if (!confirmed) {
      return;
    }

    setResuming(true);
    setError('');

    try {
      const stripeSubscriptionId = subscriptionDetails?.stripeSubscriptionId;

      if (!stripeSubscriptionId) {
        setError(t('subscription.noSubscriptionId'));
        setResuming(false);
        return;
      }

      const subscriptionIdString = String(stripeSubscriptionId).trim();

      console.log('Resuming subscription:', subscriptionIdString);

      const result = await resumeStripeSubscription(
        currentUser?.uid || '',
        subscriptionIdString
      );

      console.log('Resume result:', result);

      if (result?.success === true) {
        alert(result.message || t('stripe.resumedSuccessfully'));
        // Clear cache and reload subscription details
        clearSubscriptionCache(currentUser!.uid);
        const subscriber = await getCustomerInfo(currentUser!.uid);
        const details = getActiveSubscriptionDetails(subscriber);
        if (details) {
          saveSubscriptionToCache(currentUser!.uid, details);
        }
        setSubscriptionDetails(details);
      } else {
        const errorMessage = result?.message || t('subscription.failedToResume');
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Error resuming subscription:', err);
      setError(t('subscription.failedToResume'));
    } finally {
      setResuming(false);
    }
  };

  const getStoreName = (store: string): string => {
    const storeMap: Record<string, string> = {
      'app_store': t('subscription.storeName.appStore'),
      'play_store': t('subscription.storeName.playStore'),
      'stripe': t('subscription.storeName.stripe'),
      'promotional': t('subscription.storeName.promotional'),
    };
    return storeMap[store] || store;
  };

  const getManageSubscriptionUrl = (store: string): string | null => {
    const urlMap: Record<string, string> = {
      'app_store': 'https://apps.apple.com/account/subscriptions',
      'play_store': 'https://play.google.com/store/account/subscriptions',
    };
    return urlMap[store] || null;
  };

  const handleManageSubscription = () => {
    const url = getManageSubscriptionUrl(subscriptionDetails?.store);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      alert(t('subscription.manageSubscriptionAlert'));
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const isOnMobile = (): boolean => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  if (loading) {
    return (
      <div className="subscription-details-container">
        <div className="subscription-details-background" />
        <div className="subscription-details-wrapper" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '360px'}}>
          <LoadingRive animation="LogoFillingLoader" label={t('subscription.loading')} />
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-details-container">
      <div className="subscription-details-background" />

      <div className="subscription-details-wrapper">
        <div className="subscription-details-header">
          <div className="subscription-header">
            {deal ? (
              <DealBanner deal={deal} />
            ) : (
              <a href="https://urunn.com" style={{ cursor: 'pointer' }}>
                <img src={logo} alt="Urunn" className="urunn-logo" />
              </a>
            )}

            <button className="logout-button" onClick={handleLogout}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H6M10.6667 11.3333L14 8M14 8L10.6667 4.66667M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('common.logOut')}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="var(--color-error)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div className="error-banner-content">
              <span className="error-banner-text">{error}</span>
              <button className="error-banner-close" onClick={() => setError('')} aria-label="Dismiss error">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M13 1L1 13M1 1L13 13" stroke="var(--color-error)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="subscription-details-content">
          {!subscriptionDetails && !error ? (
          <div className="subscription-details-card">
            <p style={{ color: '#FAFDFB', textAlign: 'center', fontSize: '16px' }}>
              {t('subscription.noSubscription')}
            </p>
          </div>
        ) : subscriptionDetails && (
          <>
            <div className="subscription-details-card">
              <h1 className="subscription-title">{t('subscription.title')}</h1>

              <div className="subscription-info">
            <div className="info-row">
              <span className="info-label">{t('subscription.planLabel')}</span>
              <span className="info-value">{
               subscriptionDetails?.productIdentifier === "rc_promo_urunn_dev_premium_custom" ||
               subscriptionDetails?.productIdentifier === "rc_promo_urunn_premium_custom"
                ? t('subscription.customPlan') :
                  subscriptionDetails?.displayName || subscriptionDetails?.productIdentifier || 'Premium'}</span>
            </div>

            <div className="info-row">
              <span className="info-label">{t('subscription.statusLabel')}</span>
              <span className={`info-value ${subscriptionDetails?.willRenew ? 'status-active' : 'status-cancelled'}`}>
                {subscriptionDetails?.isInTrial
                  ? t('subscription.statusFreeTrial')
                  : subscriptionDetails?.willRenew
                    ? t('subscription.statusActive')
                    : t('subscription.statusCancelled')}
              </span>
            </div>

            <div className="info-row">
              <span className="info-label">{t('subscription.platformLabel')}</span>
              <span className="info-value">{getStoreName(subscriptionDetails?.store)}</span>
            </div>

            {subscriptionDetails?.price && (() => {
              // Handle different price formats from RevenueCat
              const price = subscriptionDetails.price;
              let amount = 0;

              // If price has currency and amount properties
              if (price.currency && price.amount !== undefined) {
                amount = typeof price.amount === 'number' ? price.amount : parseFloat(price.amount);
              }

              // Only show price if amount is greater than 0
              if (amount > 0) {
                return (
                  <div className="info-row">
                    <span className="info-label">{t('subscription.priceLabel')}</span>
                    <span className="info-value">
                      {(() => {
                        // If price has currency and amount properties
                        if (price.currency && price.amount !== undefined) {
                          const currencySymbol = getCurrencySymbol(price.currency);
                          return `${currencySymbol}${amount.toFixed(2)}`;
                        }

                        // If price is a string (e.g., "£9.99")
                        if (typeof price === 'string') {
                          return price;
                        }

                        // Fallback: display raw price data
                        return JSON.stringify(price);
                      })()}
                    </span>
                  </div>
                );
              }
              return null;
            })()}

            {subscriptionDetails?.store !== 'app_store' && subscriptionDetails?.store !== 'play_store' && (
              <>
                <div className="info-row">
                  <span className="info-label">{t('subscription.startedLabel')}</span>
                  <span className="info-value">{formatDate(subscriptionDetails?.purchaseDate)}</span>
                </div>

                <div className="info-row">
                  <span className="info-label">{subscriptionDetails?.store === 'promotional' ? t('subscription.expiryDateLabel') : (subscriptionDetails?.willRenew ? t('subscription.renewsLabel') : t('subscription.expiresLabel'))}</span>
                  <span className="info-value">{formatDate(subscriptionDetails?.expiresDate)}</span>
                </div>
              </>
            )}

            {subscriptionDetails?.isInTrial && (
              <div className="info-banner trial-banner">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 2V10L14 14M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div>
                  <strong>{t('subscription.trialBannerTitle')}</strong>
                  <p>{t('subscription.trialBannerMessage', { date: formatDate(subscriptionDetails?.expiresDate) })}</p>
                </div>
              </div>
            )}

            {!subscriptionDetails?.willRenew && !subscriptionDetails?.isInTrial && (
              <div className="info-banner cancelled-banner">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div>
                  <strong>{t('subscription.cancelledBannerTitle')}</strong>
                  <p>{t('subscription.cancelledBannerMessage', { date: formatDate(subscriptionDetails?.expiresDate) })}</p>
                </div>
              </div>
            )}

            {subscriptionDetails?.billingIssuesDetectedAt && (
              <div className="warning-message">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t('subscription.billingIssue')}</span>
              </div>
            )}
              </div>

              <div className="subscription-actions">
            {subscriptionDetails?.store === 'stripe' || subscriptionDetails?.store === 'promotional' ? (
              <>
                {(subscriptionDetails?.willRenew || subscriptionDetails?.store === 'promotional') ? (
                  <>
                    <button
                      className="change-plan-button"
                      onClick={() => navigate('/offers', { state: {
                        isChangingPlan: true,
                        currentSubscriptionExpiry: subscriptionDetails?.expiresDate,
                        currentSubscriptionStore: subscriptionDetails?.store,
                        currentPriceId: subscriptionDetails?.priceId
                      } })}
                      style={{ display: 'none' }}
                    >
                      {subscriptionDetails?.store === 'promotional' ? t('subscription.upgradePlan') : t('subscription.changePlan')}
                    </button>

                    {subscriptionDetails?.store === 'stripe' && (
                      <button
                        className="cancel-button"
                        onClick={handleCancel}
                        disabled={cancelling}
                      >
                        {cancelling ? t('subscription.cancelling') : t('subscription.cancelSubscription')}
                      </button>
                    )}

                    <p className="cancel-note">
                      {subscriptionDetails?.store === 'promotional'
                        ? t('subscription.promoNote', { date: formatDate(subscriptionDetails?.expiresDate) })
                        : t('subscription.stripeNote', { date: formatDate(subscriptionDetails?.expiresDate) })}
                    </p>
                  </>
                ) : (
                  <>
                    <button
                      className="reactivate-button"
                      onClick={handleResume}
                      disabled={resuming}
                    >
                      {resuming ? t('subscription.resuming') : t('subscription.resumeSubscription')}
                    </button>

                    <p className="cancel-note">
                      {t('subscription.accessEnds', { date: formatDate(subscriptionDetails?.expiresDate) })}
                    </p>
                  </>
                )}
              </>
            ) : subscriptionDetails?.store === 'app_store' || subscriptionDetails?.store === 'play_store' ? (
              <>
                <button
                  className="cancel-button"
                  onClick={handleManageSubscription}
                >
                  {t('subscription.manageSubscription')}
                </button>

                <p className="cancel-note">
                  {t('subscription.manageViaStore', { store: getStoreName(subscriptionDetails?.store) })}
                </p>
              </>
            ) : (
              <>
                <button
                  className="cancel-button"
                  onClick={handleCancel}
                >
                  {t('subscription.manageSubscription')}
                </button>

                <p className="cancel-note">
                  {t('subscription.manageViaStoreGeneric', { store: getStoreName(subscriptionDetails?.store) })}
                </p>
              </>
            )}
          </div>
            </div>

            {!isOnMobile() && (
              <div className="app-download-card">
                <h3 className="download-title">{t('subscription.downloadApp')}</h3>
                <div className="qr-code-container">
                  <p className="qr-code-instruction">{t('subscription.scanToDownload')}</p>
                  <img
                    src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://app.urunn.com"
                    alt="Download URUNN app QR code"
                    className="qr-code-image"
                  />
                </div>
                <a
                  className="download-app-button"
                  href={magicLink || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (!magicLink) { e.preventDefault(); return; }
                    trackStoreLinkClick('subscription', deal?.id, currentUser?.uid);
                  }}
                >
                  {t('subscription.downloadAppCta')}
                </a>
              </div>
            )}
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDetails;
